/* global HSCore */

import _ from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import axiosInstance from "../../../utils/Requests/axios";
import UserContext from "../../../utils/Contexts/UserContext";
import ThemeContext from "../../../utils/Contexts/ThemeContext";
import ImageIcon from "../../svg/ImageCheck";
import PlagiarismIcon from "../../svg/Plagiarism";
import SeoIcon from "../../svg/Seo";
import ReadIcon from "../../svg/Readability";
import AiIcon from "../../svg/AI";
import ApplyIcon from "../../svg/ApplyCheck";
import ArrowTopRightIcon from "../../svg/arrow-top-right";

function TermsSection({ termsData, setTermsData }) {
  const { t, language } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const { selectedTheme } = useContext(ThemeContext);

  const [minMaxValue, setMinMaxValue] = useState("-");
  const [isValid, setIsValid] = useState(true);
  const [editStates, setEditStates] = useState({});
  const [rerenderTomSelect, setRerenderTomSelect] = useState(false);

  const handleTermsChange = (uuid, updatedFields) => {
    const url = `sellers/${userData?.uuid}/terms/${uuid}`;
    const payload = {
      money_back_policy: updatedFields.money_back_policy || "",
      link_replacement_policy: updatedFields.link_replacement_policy || "",
      words: updatedFields.words || "",
      words_limit: updatedFields.words_limit || "",
      image: updatedFields.image || false,
      plagiarism: updatedFields.plagiarism || false,
      seo: updatedFields.seo || false,
      readability: updatedFields.readability || false,
      ai: updatedFields.ai || false,
    };

    axiosInstance
      .put(url, payload)
      .then((response) => {
        const clonedData = _.cloneDeep(termsData);
        const responseData = response.data;

        clonedData.terms.link_replacement_policy =
          responseData?.link_replacement_policy;
        clonedData.terms.money_back_policy = responseData?.money_back_policy;
        clonedData.terms.words = responseData?.words;
        clonedData.terms.words_limit = responseData?.words_limit;
        clonedData.terms.image = responseData?.image;
        clonedData.terms.plagiarism = responseData?.plagiarism;
        clonedData.terms.seo = responseData?.seo;
        clonedData.terms.readability = responseData?.readability;
        clonedData.terms.ai = responseData?.ai;
        setTermsData(clonedData);
        toast.success(t("Terms Updated."));
      })
      .catch(() => {
        toast.error(t("There was an error updating terms."));
      });
  };

  const handleMinMaxChange = (e) => {
    let value = e.target.value;
    if (value && !value.includes("-")) {
      value = `${value}-`;
    }
    setMinMaxValue(value);
    const [min, max] = value.split("-").map((v) => v.trim());
    if (min) {
      if (max && parseInt(max) > 0 && parseInt(min) > parseInt(max)) {
        setIsValid(false);
        toast.error(t("Min value should be less than max value."));
      } else {
        setIsValid(true);
      }
    } else {
      setIsValid(false);
    }
  };

  useEffect(() => {
    const initialStates = termsData?.terms?.uuid
      ? { [termsData.terms.uuid]: false } // Handle single object case
      : {};
    setEditStates(initialStates);
  }, [termsData]);

  const handleEditClick = (uuid, currentWords, currentWordsLimit) => {
    setEditStates((prevState) => ({
      ...prevState,
      [uuid]: !prevState[uuid],
    }));
    setMinMaxValue(
      `${_.isNull(currentWords) ? "" : currentWords}-${
        _.isNull(currentWordsLimit) ? "" : currentWordsLimit
      }`
    );
  };

  const handleBlur = (item) => {
    const [min, max] = minMaxValue.split("-").map((value) => value.trim());

    if (
      (_.isNull(item?.terms?.words) ? min : parseInt(min)) ===
        (_.isNull(item?.terms?.words) ? "" : item?.terms?.words) &&
      (_.isNull(item?.terms?.words_limit) ? max : parseInt(max)) ===
        (_.isNull(item?.terms?.words_limit) ? "" : item?.terms?.words_limit)
    ) {
      if (isValid)
        setEditStates((prevState) => ({
          ...prevState,
          [item?.terms?.uuid]: false,
        }));
      return;
    }

    if (min) {
      if (isValid) {
        const updatedTerms = {
          ...item?.terms,
          words: min,
          words_limit: max || null,
        };
        handleTermsChange(item?.terms?.uuid, updatedTerms);
        setIsValid(true);
        setEditStates((prevState) => ({
          ...prevState,
          [item?.terms?.uuid]: false,
        }));
      } else {
        setIsValid(false);
        toast.error(t("Add valid min and max value."));
      }
    }
  };

  const hsInitRef = useRef(null);
  useEffect(() => {
    setRerenderTomSelect(false);
    if (termsData?.terms && !hsInitRef.current) {
      const currentTermsData = { ...termsData };
      if (!_.isEqual(hsInitRef.current, currentTermsData)) {
        hsInitRef.current = currentTermsData;
        HSCore.components.HSTomSelect.init(".js-select");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [termsData, rerenderTomSelect]);

  const previousLanuage = useRef(null);
  useEffect(() => {
    if (!_.isEqual(previousLanuage.current, language)) {
      previousLanuage.current = language;
      setRerenderTomSelect(true);
    }
  }, [language]);

  useEffect(() => {
    if (!rerenderTomSelect) {
      setTermsData({ ...termsData, language: language });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerenderTomSelect]);

  const inputRef = useRef(null);
  useEffect(() => {
    if (editStates?.[termsData?.terms?.uuid] && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [editStates, termsData?.terms?.uuid]);

  return (
    <div id="termsSection" className="card card-setting">
      <div className="card-header">
        <h4 className="card-title">{t("Terms")}</h4>
      </div>
      {!rerenderTomSelect && (
        <div className="d-flex gap-2 px-3 mt-4 flex-sm-row flex-column">
          <div className="flex-fill">
            <p className="card-text">{t("Money Back Policy")}</p>

            <div className="tom-select-custom">
              <select
                className="js-select form-select form-select-sm card-text-select"
                autoComplete="off"
                data-hs-tom-select-options='{
                    "placeholder": "Select Moneyback Policy",
                    "hideSearch": true
                  }'
                onChange={(e) => {
                  const updatedTerms = {
                    ...termsData?.terms,
                    money_back_policy: e.target.value,
                  };
                  handleTermsChange(termsData?.terms?.uuid, updatedTerms);
                }}
              >
                <option
                  value={0}
                  selected={termsData?.terms?.money_back_policy === 0}
                >
                  {t("Not Provided")}
                </option>
                <option
                  value={1}
                  selected={termsData?.terms?.money_back_policy === 1}
                >
                  5 {t("days")}
                </option>
                <option
                  value={2}
                  selected={termsData?.terms?.money_back_policy === 2}
                >
                  10 {t("days")}
                </option>
                <option
                  value={3}
                  selected={termsData?.terms?.money_back_policy === 3}
                >
                  15 {t("days")}
                </option>
                <option
                  value={4}
                  selected={termsData?.terms?.money_back_policy === 4}
                >
                  20 {t("days")}
                </option>
                <option
                  value={5}
                  selected={termsData?.terms?.money_back_policy === 5}
                >
                  25 {t("days")}
                </option>
                <option
                  value={6}
                  selected={termsData?.terms?.money_back_policy === 6}
                >
                  30 {t("days")}
                </option>
                <option
                  value={7}
                  selected={termsData?.terms?.money_back_policy === 7}
                >
                  40 {t("days")}
                </option>
                <option
                  value={8}
                  selected={termsData?.terms?.money_back_policy === 8}
                >
                  50 {t("days")}
                </option>
                <option
                  value={9}
                  selected={termsData?.terms?.money_back_policy === 9}
                >
                  60 {t("days")}
                </option>
                <option
                  value={10}
                  selected={termsData?.terms?.money_back_policy === 10}
                >
                  70 {t("days")}
                </option>
                <option
                  value={11}
                  selected={termsData?.terms?.money_back_policy === 11}
                >
                  80 {t("days")}
                </option>
                <option
                  value={12}
                  selected={termsData?.terms?.money_back_policy === 12}
                >
                  90 {t("days")}
                </option>
              </select>
            </div>
          </div>
          <div className="flex-fill">
            <p className="card-text">{t("Link Replacement Policy")}</p>

            <div className="tom-select-custom">
              <select
                className="js-select form-select form-select-sm card-text-select"
                autoComplete="off"
                data-hs-tom-select-options='{
                    "placeholder": "Select Link Replacement Policy",
                    "hideSearch": true
                  }'
                onChange={(e) => {
                  const updatedTerms = {
                    ...termsData?.terms,
                    link_replacement_policy: e.target.value,
                  };
                  handleTermsChange(termsData?.terms?.uuid, updatedTerms);
                }}
              >
                <option
                  value={0}
                  selected={termsData?.terms?.money_back_policy === 0}
                >
                  {t("Not Provided")}
                </option>
                <option
                  value={1}
                  selected={termsData?.terms?.link_replacement_policy === 1}
                >
                  1 {t("month")}
                </option>
                <option
                  value={2}
                  selected={termsData?.terms?.link_replacement_policy === 2}
                >
                  2 {t("months")}
                </option>
                <option
                  value={3}
                  selected={termsData?.terms?.link_replacement_policy === 3}
                >
                  3 {t("months")}
                </option>
                <option
                  value={4}
                  selected={termsData?.terms?.link_replacement_policy === 4}
                >
                  4 {t("months")}
                </option>
                <option
                  value={5}
                  selected={termsData?.terms?.link_replacement_policy === 5}
                >
                  5 {t("months")}
                </option>
                <option
                  value={6}
                  selected={termsData?.terms?.link_replacement_policy === 6}
                >
                  6 {t("months")}
                </option>
                <option
                  value={7}
                  selected={termsData?.terms?.link_replacement_policy === 7}
                >
                  7 {t("months")}
                </option>
                <option
                  value={8}
                  selected={termsData?.terms?.link_replacement_policy === 8}
                >
                  8 {t("months")}
                </option>
                <option
                  value={9}
                  selected={termsData?.terms?.link_replacement_policy === 9}
                >
                  9 {t("months")}
                </option>
                <option
                  value={10}
                  selected={termsData?.terms?.link_replacement_policy === 10}
                >
                  10 {t("months")}
                </option>
                <option
                  value={11}
                  selected={termsData?.terms?.link_replacement_policy === 11}
                >
                  11 {t("months")}
                </option>
                <option
                  value={12}
                  selected={termsData?.terms?.link_replacement_policy === 12}
                >
                  12 {t("months")}
                </option>
              </select>
            </div>
          </div>
          <div className="flex-fill">
            <p className="card-text">{t("Number Of Words")}</p>
            {editStates?.[termsData?.terms?.uuid] === false ? (
              <button
                className="btn btn-white btn-sm w-100 text-start card-text-select"
                style={{ padding: "0.66rem" }}
                type="button"
                onClick={() =>
                  handleEditClick(
                    termsData?.terms?.uuid,
                    termsData?.terms?.words,
                    termsData?.terms?.words_limit
                  )
                }
              >
                {termsData?.terms?.words === null &&
                !termsData?.terms?.words_limit === null
                  ? "min - max"
                  : `${
                      termsData?.terms?.words === null
                        ? "min"
                        : termsData?.terms?.words
                    }
                                ${
                                  termsData?.terms?.words_limit === null
                                    ? ""
                                    : " - "
                                }
                                ${
                                  termsData?.terms?.words_limit === null
                                    ? ""
                                    : termsData?.terms?.words_limit
                                }`}
              </button>
            ) : (
              <input
                ref={inputRef}
                type="text"
                className="form-control form-control-sm w-100 card-text-select"
                value={minMaxValue}
                placeholder="min - max"
                onChange={handleMinMaxChange}
                onBlur={() => handleBlur(termsData)}
                style={{
                  borderColor: isValid ? "" : "#BA0021",
                }}
              />
            )}
          </div>
        </div>
      )}
      <div className="card-body border-bottom pb-1">
        <form>
          <div className="list-group list-group-lg list-group-flush list-group-no-gutters">
            <div className="list-group-item py-3">
              <div className="d-flex align-items-center">
                <div style={{ width: "26px", height: "26px" }}>
                  <ImageIcon />
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="row align-items-center">
                    <div className="col">
                      <h5 className="mb-2 card-text text-dark">
                        {t("Image Check")}
                      </h5>
                      <p className="responsive-text-terms text-secondary mb-0">
                        {t("Content must include at least one image")}
                      </p>
                    </div>

                    <div className="col-auto">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input me-0"
                          type="checkbox"
                          style={{ width: "51px", height: "25px" }}
                          id="connectedAccounts1"
                          checked={termsData?.terms?.image}
                          onChange={(e) => {
                            const updatedTerms = {
                              ...termsData?.terms,
                              image: e.target.checked,
                            };
                            handleTermsChange(
                              termsData?.terms?.uuid,
                              updatedTerms
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="connectedAccounts1"
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="list-group-item py-3">
              <div className="d-flex align-items-center">
                <div style={{ width: "26px", height: "26px" }}>
                  <PlagiarismIcon />
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="row align-items-center">
                    <div className="col">
                      <h5 className="mb-2 card-text text-dark">
                        {t("Plagiarism Check")}
                      </h5>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        <p className="responsive-text-terms text-secondary mb-1">
                          {t(
                            "The content must be free of copied or duplicated material from other resources."
                          )}
                        </p>
                        <span className="bg-soft-secondary text-body d-flex justify-content-center align-items-center learn-more">
                          {t("Learn more")}
                          <span className="px-1">
                            <ArrowTopRightIcon />
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="col-auto">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input me-0"
                          type="checkbox"
                          style={{ width: "51px", height: "25px" }}
                          id="connectedAccounts2"
                          checked={termsData?.terms?.plagiarism}
                          onChange={(e) => {
                            const updatedTerms = {
                              ...termsData?.terms,
                              plagiarism: e.target.checked,
                            };
                            handleTermsChange(
                              termsData?.terms?.uuid,
                              updatedTerms
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="connectedAccounts2"
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="list-group-item py-3">
              <div className="d-flex align-items-center">
                <div style={{ width: "26px", height: "26px" }}>
                  <SeoIcon />
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="row align-items-center">
                    <div className="col">
                      <h5 className="mb-2 card-text text-dark">
                        {t("SEO Check")}
                      </h5>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        <p className="responsive-text-terms text-secondary mb-1">
                          {t(
                            "The content must be follow standard SEO practices."
                          )}
                        </p>
                        <span className="bg-soft-secondary text-body d-flex justify-content-center align-items-center learn-more">
                          {t("Learn more")}
                          <span className="px-1">
                            <ArrowTopRightIcon />
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="col-auto">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input me-0"
                          type="checkbox"
                          style={{ width: "51px", height: "25px" }}
                          id="connectedAccounts2"
                          checked={termsData?.terms?.seo}
                          onChange={(e) => {
                            const updatedTerms = {
                              ...termsData?.terms,
                              seo: e.target.checked,
                            };
                            handleTermsChange(
                              termsData?.terms?.uuid,
                              updatedTerms
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="connectedAccounts2"
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="list-group-item py-3">
              <div className="d-flex align-items-center">
                <div
                  style={{ width: "26px", height: "26px", paddingLeft: "4px" }}
                >
                  <ReadIcon />
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="row align-items-center">
                    <div className="col">
                      <h5 className="mb-2 card-text text-dark">
                        {t("Readability Check")}
                      </h5>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        <p className="responsive-text-terms text-secondary mb-1">
                          {t(
                            "This check will assess the readability score to ensure the only content that is easy to read is accepted."
                          )}
                        </p>
                        <span className="bg-soft-secondary text-body d-flex justify-content-center align-items-center learn-more">
                          {t("Learn more")}
                          <span className="px-1">
                            <ArrowTopRightIcon />
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="col-auto">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input me-0"
                          type="checkbox"
                          style={{ width: "51px", height: "25px" }}
                          id="connectedAccounts2"
                          checked={termsData?.terms?.readability}
                          onChange={(e) => {
                            const updatedTerms = {
                              ...termsData?.terms,
                              readability: e.target.checked,
                            };
                            handleTermsChange(
                              termsData?.terms?.uuid,
                              updatedTerms
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="connectedAccounts2"
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="list-group-item py-3">
              <div className="d-flex align-items-center">
                <div style={{ width: "26px", height: "26px" }}>
                  <AiIcon />
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="row align-items-center">
                    <div className="col">
                      <h5 className="mb-2 card-text text-dark">
                        {t("AI Check")}
                      </h5>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        <p className="responsive-text-terms text-secondary mb-1">
                          {t(
                            "Auto decline content generated by Artificial intelligence (AI)."
                          )}
                        </p>
                        <span className="bg-soft-secondary text-body d-flex justify-content-center align-items-center learn-more">
                          {t("Learn more")}
                          <span className="px-1">
                            <ArrowTopRightIcon />
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="col-auto">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input me-0"
                          type="checkbox"
                          style={{ width: "51px", height: "25px" }}
                          id="connectedAccounts2"
                          checked={termsData?.terms?.ai}
                          onChange={(e) => {
                            const updatedTerms = {
                              ...termsData?.terms,
                              ai: e.target.checked,
                            };
                            handleTermsChange(
                              termsData?.terms?.uuid,
                              updatedTerms
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="connectedAccounts2"
                        ></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="d-flex gap-3 align-items-baseline pb-2 pt-3 justify-content-end px-4">
        <p className="responsive-text-terms text-secondary">
          {t("Would you like to apply these terms to all your other websites?")}
        </p>
        <button
          className="btn bg-soft-secondary d-flex align-items-center p-0"
          style={{
            width: "72px",
            height: "26px",
            borderRadius: "50px",
            fontSize: "12px",
            fontWeight: "400",
          }}
        >
          <span className="mx-2">{t("Apply")}</span>{" "}
          <ApplyIcon fill={selectedTheme === "dark" ? "white" : "#677788"} />
        </button>
      </div>
    </div>
  );
}

export default TermsSection;
